// src/screens/s2/PLProjection.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 10 — P&L Projection to retirement.
// Top:    assumptions panel (revenue growth, gross margin %, overhead growth
//         rate, plus locked pension + extraction).
// Middle: stacked-area projection chart (revenue → gross profit → EBITDA →
//         net profit) with pension contribution line overlay, 2-year
//         historical muted to the left, dashed vertical at "today".
// Below:  two mini trend lines (gross margin % and overhead % of revenue).
// Foot:   four headline cards — revenue at retirement, EBITDA at retirement,
//         cumulative pension funded, cumulative CT saved.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, LineChart, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { useXeroSales } from '../../hooks/useXeroSales';
import { effectiveCTRate } from '../../taxConfig';
import { formatCurrency, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function PLProjection({ data, onUpdate }: Props) {
  const xero = useXeroData(data.xeroTenantId);
  const sales = useXeroSales(data.xeroTenantId);

  if (xero.loading || sales.loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading P&L projection data…</div>;
  }
  if (!xero.mapped || !xero.summary) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">P&amp;L Projection</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {xero.error || 'Projection needs live Xero P&L.'}
        </div>
      </div>
    );
  }

  const s = xero.summary!;
  const ykpis = sales.data?.yearlyKPIs || [];

  // ── Assumptions (with defaults derived from yearlyKPIs where possible) ─
  const completeYears = ykpis.filter((y) => !y.year.endsWith('YTD'));
  const impliedGrowth = completeYears.length >= 2
    ? Math.max(-0.1, Math.min(0.5, (completeYears[completeYears.length - 1].revenue / completeYears[0].revenue) ** (1 / (completeYears.length - 1)) - 1))
    : 0.05;
  const revenueGrowth = data.s2RevenueGrowthRate ?? Math.round(impliedGrowth * 1000) / 1000;
  const grossMarginPct = completeYears.length > 0 ? completeYears[completeYears.length - 1].grossMargin : 90;
  const overheadGrowth = data.s2OverheadGrowthRate ?? 0.025;

  // Locked inputs — cascade from S2 capital allocation + S1 extraction.
  const s2PensionMonthly = data.s2PensionMonthly ?? (s.ownerPension / 12);
  const annualPension = s2PensionMonthly * 12;
  const directorExtraction = s.ownerSalary + (data.s2DividendMonthly ?? 0) * 12;   // salary + dividend SO

  // ── Build projection rows ──────────────────────────────────────────────
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const thisYear = new Date().getFullYear();
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);

  interface Row {
    year: number; phase: 'history' | 'projection';
    revenue: number; grossProfit: number; ebitda: number; netProfit: number;
    pension: number; grossMarginPct: number; overheadPct: number;
  }
  const rows: Row[] = [];

  // Historical points (from yearlyKPIs) — map each FY start year to a row.
  completeYears.forEach((fy) => {
    const fyStartYear = parseInt(fy.year.split('/')[0], 10);
    const rev = fy.revenue;
    const gp = rev * (fy.grossMargin / 100);
    const overhead = rev * (fy.overheadRatio / 100);
    const ebitda = rev * (fy.ebitdaMargin / 100);
    const np = ebitda * 0.95;    // rough: ebitda less finance/dep approximation
    rows.push({
      year: fyStartYear,
      phase: 'history',
      revenue: Math.round(rev),
      grossProfit: Math.round(gp),
      ebitda: Math.round(ebitda),
      netProfit: Math.round(np),
      pension: 0,                // historic pension not tracked annually in KPI
      grossMarginPct: fy.grossMargin,
      overheadPct: fy.overheadRatio,
    });
  });

  // Projection — start from current summary, step forward.
  let projRev = s.revenue;
  let projOverheads = s.totalOperating + s.staffCosts + directorExtraction;
  for (let i = 0; i <= yearsToFreedom; i++) {
    const year = thisYear + i;
    if (i > 0) {
      projRev = projRev * (1 + revenueGrowth);
      projOverheads = projOverheads * (1 + overheadGrowth);
    }
    const gp = projRev * (grossMarginPct / 100);
    const ebitda = gp - projOverheads - annualPension;
    const np = ebitda - s.financeAndDepreciation;
    rows.push({
      year,
      phase: 'projection',
      revenue: Math.round(projRev),
      grossProfit: Math.round(gp),
      ebitda: Math.round(ebitda),
      netProfit: Math.round(np),
      pension: Math.round(annualPension),
      grossMarginPct: projRev > 0 ? (gp / projRev) * 100 : 0,
      overheadPct: projRev > 0 ? (projOverheads / projRev) * 100 : 0,
    });
  }

  // ── Headline totals ────────────────────────────────────────────────────
  const projectionRows = rows.filter((r) => r.phase === 'projection');
  const finalRow = projectionRows[projectionRows.length - 1];
  const cumulativePension = projectionRows.reduce((s2, r) => s2 + r.pension, 0);
  const cumulativeCtSaved = projectionRows.reduce((s2, r) => s2 + r.pension * effectiveCTRate(r.netProfit), 0);

  const todayYear = thisYear;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          P&amp;L Projection
          {xero.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Annual revenue, gross profit, EBITDA and net profit projected to
          freedom age {freedomAge} under the assumptions below.
        </p>
      </div>

      {/* ASSUMPTIONS PANEL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Assumptions</div>
        <div className="grid grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Revenue growth rate</label>
            <div className="flex items-baseline gap-1">
              {/* Display as % (×100), store as decimal (÷100). */}
              <input
                type="number"
                step="0.5"
                value={(revenueGrowth * 100).toFixed(1)}
                onChange={(e) => onUpdate({ s2RevenueGrowthRate: (parseFloat(e.target.value || '0') || 0) / 100 })}
                className="text-lg font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-20"
              />
              <span className="text-xs text-gray-500">%/yr</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Implied {formatPercent(impliedGrowth * 100)}</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Gross margin %</label>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-ma-navy">{grossMarginPct}</span>
              <span className="text-xs text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Held flat (last FY)</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Overhead growth</label>
            <div className="flex items-baseline gap-1">
              {/* Display as % (×100), store as decimal (÷100). */}
              <input
                type="number"
                step="0.5"
                value={(overheadGrowth * 100).toFixed(1)}
                onChange={(e) => onUpdate({ s2OverheadGrowthRate: (parseFloat(e.target.value || '0') || 0) / 100 })}
                className="text-lg font-bold text-ma-navy bg-transparent border border-gray-300 rounded px-2 py-1 w-20"
              />
              <span className="text-xs text-gray-500">%/yr</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Default 2.5%</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pension (locked)</label>
            <div className="text-lg font-bold text-gray-700">{formatCurrency(s2PensionMonthly)}/mo</div>
            <p className="text-xs text-gray-400 mt-1">From capital allocation</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Extraction (locked)</label>
            <div className="text-lg font-bold text-gray-700">{formatCurrency(directorExtraction)}/yr</div>
            <p className="text-xs text-gray-400 mt-1">Salary + S1 dividend SO</p>
          </div>
        </div>
      </div>

      {/* PROJECTION CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">P&amp;L to freedom age</h3>
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 20, right: 20, bottom: 8, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine x={todayYear} stroke="#9CA3AF" strokeDasharray="4 2" label={{ value: 'Today', fontSize: 10, fill: '#6B7280', position: 'top' }} />
              {/* isAnimationActive={false} on every series — Recharts 3.x
                  animation-state bug drops Line/Area stroke paths until the
                  animation finishes; disabling it stabilises the render. */}
              <Area type="monotone" dataKey="revenue" stackId="pl" stroke="#60A5FA" fill="#60A5FA" fillOpacity={0.6} isAnimationActive={false} />
              <Area type="monotone" dataKey="grossProfit" stackId="pl2" stroke="#10B981" fill="#10B981" fillOpacity={0.6} isAnimationActive={false} />
              <Area type="monotone" dataKey="ebitda" stackId="pl3" stroke="#1E3A5F" fill="#1E3A5F" fillOpacity={0.7} isAnimationActive={false} />
              <Area type="monotone" dataKey="netProfit" stackId="pl4" stroke="#C9A84C" fill="#C9A84C" fillOpacity={0.8} isAnimationActive={false} />
              <Line type="monotone" dataKey="pension" stroke="#1B2B4B" strokeWidth={2} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Historical FYs on the left, projection to right of dashed Today line.
          Pension contribution (dashed navy line) steps with business growth.
        </p>
      </div>

      {/* KPI TREND LINES */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-xs font-bold text-ma-navy uppercase tracking-wide mb-2">Gross margin %</h3>
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={rows} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: any) => `${v.toFixed ? v.toFixed(1) : v}%`} />
                <ReferenceLine x={todayYear} stroke="#9CA3AF" strokeDasharray="4 2" />
                <Line type="monotone" dataKey="grossMarginPct" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-xs font-bold text-ma-navy uppercase tracking-wide mb-2">Overhead % of revenue</h3>
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={rows} margin={{ top: 10, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 9 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: any) => `${v.toFixed ? v.toFixed(1) : v}%`} />
                <ReferenceLine x={todayYear} stroke="#9CA3AF" strokeDasharray="4 2" />
                <Line type="monotone" dataKey="overheadPct" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* HEADLINE CARDS */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Revenue at freedom</div>
          <div className="text-xl font-bold text-ma-navy">{finalRow ? formatCurrency(finalRow.revenue) : '—'}</div>
          <div className="text-xs text-gray-500 mt-1">Age {freedomAge}</div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">EBITDA at freedom</div>
          <div className="text-xl font-bold text-ma-navy">{finalRow ? formatCurrency(finalRow.ebitda) : '—'}</div>
          <div className="text-xs text-gray-500 mt-1">Annual</div>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#10B981', backgroundColor: '#ECFDF5' }}>
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">Cumulative pension funded</div>
          <div className="text-xl font-bold text-emerald-700">{formatCurrency(cumulativePension)}</div>
          <div className="text-xs text-emerald-700 mt-1">Over {yearsToFreedom} years</div>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#C9A84C', backgroundColor: '#FFF9ED' }}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>Cumulative CT saved</div>
          <div className="text-xl font-bold" style={{ color: '#C9A84C' }}>{formatCurrency(cumulativeCtSaved)}</div>
          <div className="text-xs mt-1" style={{ color: '#8B7A3E' }}>Year-by-year effective rate</div>
        </div>
      </div>
    </div>
  );
}
